import { inject, Injectable } from '@angular/core';
import { PlatformService } from '@sml/utility';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

/** Lifecycle phase of the form-submission overlay. */
export type FormSpinnerMode = 'hidden' | 'submitting' | 'success' | 'error';

/** Current overlay state plus the copy shown in the success/error phases. */
export interface FormSpinnerState {
  mode: FormSpinnerMode;
  /** Headline for the success/error phase (e.g. 'Payment Complete!'). */
  title?: string;
  /** Supporting line beneath the headline. */
  message?: string;
  /** While submitting, shows a `$` in the loader core (payment) vs the logo (form). */
  isPayment?: boolean;
}

const HIDDEN: FormSpinnerState = { mode: 'hidden' };

/**
 * Drives the full-screen form-submission overlay ({@link FormSpinnerComponent}).
 * The overlay stays mounted on a single aurora backdrop and morphs between the
 * submitting loader and the success/error result, so the user never sees a
 * jarring switch to a separate dialog.
 */
@Injectable({
  providedIn: 'root',
})
export class FormSpinnerService {
  private platformService = inject(PlatformService);

  private readonly formSpinnerState$ = new BehaviorSubject<FormSpinnerState>(HIDDEN);

  /** Show the submitting loader. Pass `isPayment` to show `$` in the loader core. */
  public showFormSpinner(config?: { isPayment?: boolean }): void {
    this.formSpinnerState$.next({ mode: 'submitting', isPayment: !!config?.isPayment });
  }

  /** Morph the overlay into its celebratory success state. */
  public showSuccess(config?: { title?: string; message?: string }): void {
    this.formSpinnerState$.next({
      mode: 'success',
      title: config?.title,
      message: config?.message,
    });
  }

  /** Morph the overlay into an error state so the user can dismiss it. */
  public showError(config?: { title?: string; message?: string }): void {
    this.formSpinnerState$.next({
      mode: 'error',
      title: config?.title,
      message: config?.message,
    });
  }

  /** Dismiss the overlay entirely. */
  public hideFormSpinner(): void {
    if (this.platformService.browser()) {
      this.formSpinnerState$.next(HIDDEN);
    }
  }

  public getFormSpinnerState(): Observable<FormSpinnerState> {
    return this.formSpinnerState$.asObservable();
  }
}
